import * as React from "react";
import { LifeBuoy, MoreHorizontal, Send } from "lucide-react";
import {
  Button,
  DataTable,
  DataTableEmpty,
  DataTableFooter,
  DataTablePagination,
  DataTableScroll,
  DataTableSkeleton,
  DataTableToolbar,
  DataTableToolbarSpacer,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  PageHeader,
  PageHeaderDescription,
  PageHeaderHeading,
  PageHeaderTitle,
  PageHeaderTop,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  StatusPill,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
  toast,
} from "@eduviaos/ui-kit";
import {
  useMarkPlatformTicketRead,
  usePlatformMessages,
  usePlatformSupportRealtime,
  usePlatformTickets,
  useSendPlatformMessage,
  useUpdatePlatformTicket,
  openPlatformFile,
  uploadPlatformFile,
  type PlatformTicket,
} from "@/lib/support/api";
import { useAuth } from "@/auth/AuthProvider";

function formatWhen(value: string): string {
  return new Intl.DateTimeFormat("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

export function SupportInboxPage() {
  const { user } = useAuth();
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(10);
  const tickets = usePlatformTickets({
    limit: pageSize,
    offset: (page - 1) * pageSize,
  });
  const send = useSendPlatformMessage();
  const update = useUpdatePlatformTicket();
  const { mutate: markRead } = useMarkPlatformTicketRead();
  const [active, setActive] = React.useState<PlatformTicket | null>(null);
  const [reply, setReply] = React.useState("");
  const [replyFiles, setReplyFiles] = React.useState<File[]>([]);
  const messages = usePlatformMessages(active?.id ?? null);
  usePlatformSupportRealtime(active?.id ?? null);

  React.useEffect(() => {
    if (!active || active.responder_unread_count === 0) return;
    markRead(active.id);
  }, [active, markRead]);

  const sendReply = async () => {
    if (!active || (!reply.trim() && replyFiles.length === 0)) return;
    const body = reply.trim();
    const chosen = replyFiles;
    setReply("");
    setReplyFiles([]);
    try {
      const attachments = [];
      for (const file of chosen) {
        attachments.push(await uploadPlatformFile(active.id, file));
      }
      await send.mutateAsync({
        ticketId: active.id,
        body,
        clientMessageId: crypto.randomUUID(),
        attachments,
      });
    } catch (error) {
      setReply(body);
      setReplyFiles(chosen);
      toast.error(error instanceof Error ? error.message : "Could not send the reply.");
    }
  };

  const rows = tickets.data?.items ?? [];

  return (
    <div className="space-y-6 p-6">
      <PageHeader>
        <PageHeaderTop>
          <PageHeaderTitle>
            <PageHeaderHeading>Centre support</PageHeaderHeading>
            <PageHeaderDescription>
              Requests sent by institution teams to EduViaOS.
            </PageHeaderDescription>
          </PageHeaderTitle>
        </PageHeaderTop>
      </PageHeader>

      <DataTable>
        <DataTableToolbar>
          <LifeBuoy aria-hidden="true" className="text-muted-foreground size-4" />
          <span className="text-muted-foreground text-sm">All centre requests</span>
          <DataTableToolbarSpacer />
        </DataTableToolbar>
        {tickets.isLoading ? (
          <DataTableSkeleton columns={7} rows={8} />
        ) : rows.length === 0 ? (
          <DataTableEmpty
            icon={<LifeBuoy aria-hidden="true" />}
            title="No centre requests"
            description="New institution support conversations will appear here."
          />
        ) : (
          <>
            <div className="space-y-3 p-3 sm:hidden">
              {rows.map((ticket) => (
                <article key={ticket.id} className="border-border/60 rounded-xl border p-4">
                  <div className="flex items-start justify-between gap-3">
                    <button
                      type="button"
                      className="text-left font-medium hover:underline"
                      onClick={() => setActive(ticket)}
                    >
                      {ticket.subject}
                    </button>
                    <StatusPill
                      status={
                        ticket.status === "open"
                          ? "active"
                          : ticket.status === "in_progress"
                            ? "invited"
                            : ticket.status === "resolved"
                              ? "inactive"
                              : "archived"
                      }
                      label={ticket.status.replace("_", " ")}
                    />
                  </div>
                  <p className="text-muted-foreground mt-2 text-xs">
                    {ticket.tenant_id} · {ticket.requester_name}
                  </p>
                  <div className="text-muted-foreground mt-3 flex justify-between gap-3 text-xs">
                    <span className="tabular-nums">
                      {ticket.message_count} message{ticket.message_count === 1 ? "" : "s"}
                      {ticket.responder_unread_count > 0
                        ? ` · ${ticket.responder_unread_count} new`
                        : ""}
                    </span>
                    <time dateTime={ticket.last_message_at ?? ticket.created_at}>
                      {formatWhen(ticket.last_message_at ?? ticket.created_at)}
                    </time>
                  </div>
                </article>
              ))}
            </div>
            <div className="hidden sm:block">
              <DataTableScroll>
                <Table>
                  <TableCaption className="sr-only">Centre support requests</TableCaption>
                  <TableHeader className="bg-background sticky top-0 z-10">
                    <TableRow>
                      <TableHead className="bg-background sticky left-0 z-20 shadow-[inset_-1px_0_0_hsl(var(--border))]">
                        Subject
                      </TableHead>
                      <TableHead>Tenant</TableHead>
                      <TableHead>Requester</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Messages</TableHead>
                      <TableHead className="text-right">Updated</TableHead>
                      <TableHead className="w-[1%] text-right">
                        <span className="sr-only">Actions</span>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody className="divide-border/60 divide-y">
                    {rows.map((ticket) => (
                      <TableRow key={ticket.id} className="hover:bg-muted/40">
                        <TableCell className="bg-background sticky left-0 z-10 shadow-[inset_-1px_0_0_hsl(var(--border))]">
                          <button
                            type="button"
                            className="font-medium hover:underline"
                            onClick={() => setActive(ticket)}
                          >
                            {ticket.subject}
                          </button>
                        </TableCell>
                        <TableCell>{ticket.tenant_id}</TableCell>
                        <TableCell>{ticket.requester_name}</TableCell>
                        <TableCell>
                          <StatusPill
                            status={
                              ticket.status === "open"
                                ? "active"
                                : ticket.status === "in_progress"
                                  ? "invited"
                                  : ticket.status === "resolved"
                                    ? "inactive"
                                    : "archived"
                            }
                            label={ticket.status.replace("_", " ")}
                          />
                        </TableCell>
                        <TableCell
                          className="text-right tabular-nums"
                          title={ticket.last_message_at ?? ticket.created_at}
                        >
                          {ticket.message_count}
                          {ticket.responder_unread_count > 0
                            ? ` (${ticket.responder_unread_count} new)`
                            : ""}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatWhen(ticket.last_message_at ?? ticket.created_at)}
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                aria-label={`Actions for ${ticket.subject}`}
                              >
                                <MoreHorizontal aria-hidden="true" className="size-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {ticket.assigned_to !== user?.id ? (
                                <DropdownMenuItem
                                  disabled={!user || update.isPending}
                                  onSelect={() =>
                                    user
                                      ? update.mutate({
                                          ticketId: ticket.id,
                                          assignedTo: user.id,
                                        })
                                      : undefined
                                  }
                                >
                                  Assign to me
                                </DropdownMenuItem>
                              ) : null}
                              {ticket.assigned_to ? (
                                <DropdownMenuItem
                                  disabled={update.isPending}
                                  onSelect={() =>
                                    update.mutate({
                                      ticketId: ticket.id,
                                      assignedTo: null,
                                    })
                                  }
                                >
                                  Unassign
                                </DropdownMenuItem>
                              ) : null}
                              {(["open", "in_progress", "resolved", "closed"] as const).map(
                                (status) => (
                                  <DropdownMenuItem
                                    key={status}
                                    disabled={ticket.status === status || update.isPending}
                                    onSelect={() => update.mutate({ ticketId: ticket.id, status })}
                                  >
                                    Mark {status.replace("_", " ")}
                                  </DropdownMenuItem>
                                ),
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </DataTableScroll>
            </div>
          </>
        )}
        <DataTableFooter>
          <span className="text-muted-foreground text-xs">0 of {rows.length} row(s) selected.</span>
          <DataTablePagination
            page={page}
            pageSize={pageSize}
            totalRows={tickets.data?.total ?? 0}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
          />
        </DataTableFooter>
      </DataTable>

      <Sheet
        open={active !== null}
        onOpenChange={(open) => {
          if (!open) {
            setActive(null);
            setReply("");
          }
        }}
      >
        <SheetContent side="right" className="flex w-full flex-col p-0 sm:max-w-2xl">
          <SheetHeader className="border-border/60 border-b p-6">
            <SheetTitle>{active?.subject ?? "Centre request"}</SheetTitle>
            <SheetDescription>
              {active?.tenant_id} · {active?.requester_name}
            </SheetDescription>
            {active ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="mt-3 w-fit">
                    Manage request
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  {active.assigned_to !== user?.id ? (
                    <DropdownMenuItem
                      disabled={!user || update.isPending}
                      onSelect={() =>
                        user
                          ? update.mutate(
                              {
                                ticketId: active.id,
                                assignedTo: user.id,
                              },
                              { onSuccess: (ticket) => setActive(ticket) },
                            )
                          : undefined
                      }
                    >
                      Assign to me
                    </DropdownMenuItem>
                  ) : null}
                  {active.assigned_to ? (
                    <DropdownMenuItem
                      disabled={update.isPending}
                      onSelect={() =>
                        update.mutate(
                          {
                            ticketId: active.id,
                            assignedTo: null,
                          },
                          { onSuccess: (ticket) => setActive(ticket) },
                        )
                      }
                    >
                      Unassign
                    </DropdownMenuItem>
                  ) : null}
                  {(["open", "in_progress", "resolved", "closed"] as const).map((status) => (
                    <DropdownMenuItem
                      key={status}
                      disabled={active.status === status || update.isPending}
                      onSelect={() => {
                        update.mutate(
                          { ticketId: active.id, status },
                          {
                            onSuccess: (ticket) => setActive(ticket),
                          },
                        );
                      }}
                    >
                      Mark {status.replace("_", " ")}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
          </SheetHeader>
          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-6">
            {(messages.data?.items ?? []).map((message) => (
              <article key={message.id} className="border-border/60 rounded-xl border p-4">
                <div className="flex justify-between gap-3 text-xs">
                  <strong>{message.author_name}</strong>
                  <time className="text-muted-foreground" dateTime={message.created_at}>
                    {formatWhen(message.created_at)}
                  </time>
                </div>
                <p className="mt-2 whitespace-pre-wrap text-sm">{message.body}</p>
                {(message.attachments ?? []).length > 0 ? (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {(message.attachments ?? []).map((file) => (
                      <Button
                        key={file.object_path}
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          void openPlatformFile(active?.id ?? "", file.object_path).catch(() =>
                            toast.error("Could not open that file."),
                          )
                        }
                      >
                        {file.filename}
                      </Button>
                    ))}
                  </div>
                ) : null}
                <span className="text-muted-foreground mt-2 block text-[10px]">
                  {message.delivery_status === "sending"
                    ? "Sending…"
                    : message.moderation_status === "visible"
                      ? "Delivered"
                      : "Under review"}
                </span>
              </article>
            ))}
          </div>
          <div className="border-border/60 space-y-3 border-t p-6">
            <Textarea
              aria-label="Reply to centre"
              value={reply}
              onChange={(event) => setReply(event.target.value)}
              rows={4}
              maxLength={4000}
            />
            <label htmlFor="platform-support-files" className="cursor-pointer text-sm underline">
              Add image or document
            </label>
            <input
              id="platform-support-files"
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif,application/pdf,text/plain,.doc,.docx,.xlsx"
              multiple
              className="sr-only"
              onChange={(event) => {
                const picked = Array.from(event.target.files ?? []);
                setReplyFiles((current) => [...current, ...picked].slice(0, 4));
                event.target.value = "";
              }}
            />
            {replyFiles.map((file) => (
              <span
                key={`${file.name}-${file.size}`}
                className="text-muted-foreground block text-xs"
              >
                {file.name}
              </span>
            ))}
            <Button
              className="w-full"
              disabled={(!reply.trim() && replyFiles.length === 0) || send.isPending}
              onClick={() => void sendReply()}
            >
              <Send aria-hidden="true" className="size-4" />
              {send.isPending ? "Sending…" : "Send reply"}
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
